/**
 * @page FlexZusagePage
 * @route /flex/zusage/:token (public, no auth)
 * @description Public Einsatz-Zusage-Seite. Mobile-optimiert.
 *   Token-basiert: Worker klickt Link aus E-Mail/SMS → sieht Einsatz → sagt zu.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Briefcase, Info } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function formatDateDE(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-AT', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  return String(t).substring(0, 5);
}

export default function FlexZusagePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, ready, confirmed, full, invalid, error
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/flex/zusage/${token}`);
        if (res.status === 404) { setStatus('invalid'); return; }
        if (!res.ok) { setStatus('error'); return; }
        const d = await res.json();
        setData(d);
        if (d.zusage_status === 'zugesagt') setStatus('confirmed');
        else if (!d.platz_frei) setStatus('full');
        else setStatus('ready');
      } catch { setStatus('error'); }
    })();
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await fetch(`${API}/api/public/flex/zusage/${token}`, { method: 'POST' });
      if (res.ok) {
        setStatus('confirmed');
      } else if (res.status === 409) {
        setStatus('full');
      } else {
        const d = await res.json().catch(() => ({}));
        if (d.status === 'zugesagt') setStatus('confirmed');
        else setStatus('error');
      }
    } catch { setStatus('error'); }
    finally { setConfirming(false); }
  };

  const e = data?.einsatz;
  const pct = data ? Math.min(100, Math.round((data.zugesagt / data.benoetigt) * 100)) : 0;
  const frei = data ? Math.max(0, data.benoetigt - data.zugesagt) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F5FA] to-white flex flex-col">
      {/* Logo bar */}
      <div className="w-full px-6 py-5 flex items-center justify-center">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#338edc] to-[#6366F1] flex items-center justify-center">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <span className="text-[15px] font-semibold text-[#1A1A2E]">WeShip</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-md">

          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center py-20 text-[#9CA3AF]">...</div>
          )}

          {/* Invalid token */}
          {status === 'invalid' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#128279;</div>
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Dieser Link ist nicht mehr gültig.</h2>
              <p className="text-sm text-[#6B7280]">Bei Fragen: jobs@email.weship.eu</p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Ein Fehler ist aufgetreten.</h2>
              <p className="text-sm text-[#6B7280]">Bitte versuche es später erneut oder kontaktiere jobs@email.weship.eu</p>
            </div>
          )}

          {/* Ready / Confirmed / Full */}
          {(status === 'ready' || status === 'confirmed' || status === 'full') && e && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] overflow-hidden">
              {/* Greeting */}
              <div className="px-6 pt-6 pb-4">
                <h1 className="text-xl font-semibold text-[#1A1A2E]">
                  Hallo {data.bewerber_name?.split(' ')[0]}!
                </h1>
              </div>

              {/* Einsatz info */}
              <div className="px-6 pb-6 space-y-4">
                <h2 className="text-lg font-semibold text-[#1A1A2E]">{e.titel}</h2>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-[#338edc] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#1A1A2E]">
                      {formatDateDE(e.datum_von)}{e.datum_bis && e.datum_bis !== e.datum_von ? ` – ${formatDateDE(e.datum_bis)}` : ''}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={18} className="text-[#338edc] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#1A1A2E]">{formatTime(e.uhrzeit_von)} – {formatTime(e.uhrzeit_bis)} Uhr</span>
                  </div>
                  {e.aufgabe && (
                    <div className="flex items-start gap-3">
                      <Briefcase size={18} className="text-[#338edc] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#1A1A2E]">{e.aufgabe}</span>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#338edc] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#1A1A2E]">Frikusweg 10, 8141 Unterpremstätten</span>
                  </div>
                  {e.zusatz_info && (
                    <div className="flex items-start gap-3">
                      <Info size={18} className="text-[#338edc] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#1A1A2E]">{e.zusatz_info}</span>
                    </div>
                  )}
                </div>

                {/* Progress bar */}
                <div className="pt-4">
                  <p className="text-sm text-[#6B7280] mb-2">
                    {data.zugesagt} von {data.benoetigt} Plätzen vergeben
                  </p>
                  <div className="w-full h-3 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? '#10B981' : '#338edc' }}
                    />
                  </div>
                  {status === 'ready' && frei > 0 && (
                    <p className="text-sm font-medium text-[#338edc] mt-2">
                      Noch {frei} Platz{frei !== 1 ? 'e' : ''} frei!
                    </p>
                  )}
                </div>
              </div>

              {/* Action area */}
              <div className="px-6 pb-6">
                {status === 'ready' && (
                  <button
                    onClick={handleConfirm}
                    disabled={confirming}
                    className="w-full py-4 rounded-xl bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold text-base disabled:opacity-50 transition-colors"
                  >
                    {confirming ? '...' : 'Ich bin dabei!'}
                  </button>
                )}

                {status === 'confirmed' && (
                  <div className="bg-[#D1FAE5] rounded-xl p-5 text-center">
                    <div className="text-2xl mb-2">&#9989;</div>
                    <p className="font-semibold text-[#065F46]">Du hast zugesagt.</p>
                    <p className="text-sm text-[#047857] mt-1">
                      Wir freuen uns auf dich am {formatDateDE(e.datum_von)}.
                    </p>
                  </div>
                )}

                {status === 'full' && (
                  <div className="bg-[#FEF3C7] rounded-xl p-5 text-center">
                    <div className="text-2xl mb-2">&#128532;</div>
                    <p className="font-semibold text-[#92400E]">Leider sind bereits alle Plätze vergeben.</p>
                    <p className="text-sm text-[#B45309] mt-1">Beim nächsten Mal bist du schneller! Wir melden uns bald wieder.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-xs text-[#9CA3AF] space-y-1">
            <p>Fragen? jobs@email.weship.eu</p>
            <p>&copy; 2026 We Ship Fulfillment GmbH</p>
          </div>
        </div>
      </div>
    </div>
  );
}
